import React, { useState, useRef, useEffect } from 'react';

const NewsCard = ({ searchResults, onGeneratePodcast, audioUrl = null, podcastState = 'idle', onOpenPlayer }) => {
  // State
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageError, setImageError] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);

  // Early return
  if (!searchResults || searchResults.length === 0) {
    return null;
  }

  const currentArticle = searchResults[currentIndex];

  // Helper functions
  const getCategoryFallback = (source, title) => {
    const text = (source + ' ' + title).toLowerCase();
    const categoryMap = {
      technology: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=400&h=250&fit=crop&q=80',
      ai: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=250&fit=crop&q=80',
      software: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=400&h=250&fit=crop&q=80',
      business: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=250&fit=crop&q=80',
      finance: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=400&h=250&fit=crop&q=80',
      science: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=400&h=250&fit=crop&q=80',
      health: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&h=250&fit=crop&q=80',
      gaming: 'https://images.unsplash.com/photo-1511512578047-dfb367046420?w=400&h=250&fit=crop&q=80',
      entertainment: 'https://images.unsplash.com/photo-1514306191717-452ec28c7814?w=400&h=250&fit=crop&q=80',
      sports: 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=400&h=250&fit=crop&q=80',
      crypto: 'https://images.unsplash.com/photo-1518546305927-5a555bb7020d?w=400&h=250&fit=crop&q=80',
      news: 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=400&h=250&fit=crop&q=80'
    };
    for (const [key, url] of Object.entries(categoryMap)) {
      if (text.includes(key)) return url;
    }
    return categoryMap.news;
  };

  const getImageUrl = (article) => {
    if (imageError[currentIndex] || !article.imageUrl) {
      return getCategoryFallback(article.source, article.title);
    }
    return article.imageUrl;
  };

  const gradients = [
    'linear-gradient(135deg, #fce7f3 0%, #dbeafe 50%, #fef3c7 100%)',
    'linear-gradient(135deg, #e0f2fe 0%, #e0e7ff 50%, #fce7f3 100%)',
    'linear-gradient(135deg, #dfe7fd 0%, #fedce7 50%, #ffe4e6 100%)',
    'linear-gradient(135deg, #fef3c7 0%, #fce7f3 50%, #dbeafe 100%)',
    'linear-gradient(135deg, #d1fae5 0%, #fef3c7 50%, #fecaca 100%)',
  ];

  return (
    <div style={{
      marginTop: '12px',
      background: gradients[currentIndex % gradients.length],
      borderRadius: '16px',
      padding: '14px',
      maxWidth: '330px',
      width: '100%',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      border: '1px solid rgba(224, 231, 252, 0.5)',
      transition: 'all 0.3s ease'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '10px',
        padding: '8px 12px',
        background: 'rgba(255, 255, 255, 0.7)',
        borderRadius: '8px',
        border: '1px solid rgba(224, 231, 252, 0.4)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{
            width: '6px',
            height: '6px',
            background: 'linear-gradient(135deg, #ff4757 0%, #ff6348 100%)',
            borderRadius: '50%',
            boxShadow: '0 0 8px rgba(255, 71, 87, 0.6)',
            animation: 'pulse 2s ease-in-out infinite'
          }} />
          <span style={{
            fontSize: '10px',
            fontWeight: '700',
            color: '#1f2937',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Latest News
          </span>
        </div>
        <div style={{
          fontSize: '9px',
          fontWeight: '600',
          color: '#6b7280',
          padding: '3px 8px',
          background: 'rgba(255, 255, 255, 0.6)',
          borderRadius: '6px'
        }}>
          {currentIndex + 1} / {searchResults.length}
        </div>
      </div>

      {/* Article Card */}
      <div style={{
        position: 'relative',
        background: 'white',
        borderRadius: '10px',
        overflow: 'hidden',
        marginBottom: '20px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
      }}>
        {/* Nav Arrows */}
        {searchResults.length > 1 && (
          <>
            <button
              onClick={() => {
                setCurrentIndex(prev => Math.max(0, prev - 1));
                setImageError({});
              }}
              disabled={currentIndex === 0}
              style={{
                position: 'absolute',
                left: '8px',
                top: '30%',
                transform: 'translateY(-50%)',
                width: '28px',
                height: '28px',
                background: currentIndex === 0 ? 'rgba(200, 200, 200, 0.3)' : '#ffffff7d',
                border: '1px solid rgba(226, 232, 240, 0.8)',
                borderRadius: '50%',
                cursor: currentIndex === 0 ? 'not-allowed' : 'pointer',
                opacity: currentIndex === 0 ? 0.4 : 1,
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <svg width="14" height="14" viewBox="0 0 15 15" fill="none">
                <path d="M8.84182 3.13514C9.04327 3.32401 9.05348 3.64042 8.86462 3.84188L5.43521 7.49991L8.86462 11.1579C9.05348 11.3594 9.04327 11.6758 8.84182 11.8647C8.64036 12.0535 8.32394 12.0433 8.13508 11.8419L4.38508 7.84188C4.20477 7.64955 4.20477 7.35027 4.38508 7.15794L8.13508 3.15794C8.32394 2.95648 8.64036 2.94628 8.84182 3.13514Z" fill="currentColor" />
              </svg>
            </button>

            <button
              onClick={() => {
                setCurrentIndex(prev => Math.min(searchResults.length - 1, prev + 1));
                setImageError({});
              }}
              disabled={currentIndex === searchResults.length - 1}
              style={{
                position: 'absolute',
                right: '8px',
                top: '30%',
                transform: 'translateY(-50%)',
                width: '28px',
                height: '28px',
                background: currentIndex === searchResults.length - 1 ? 'rgba(200, 200, 200, 0.3)' : '#ffffff7d',
                border: '1px solid rgba(226, 232, 240, 0.8)',
                borderRadius: '50%',
                cursor: currentIndex === searchResults.length - 1 ? 'not-allowed' : 'pointer',
                opacity: currentIndex === searchResults.length - 1 ? 0.4 : 1,
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <svg width="14" height="14" viewBox="0 0 15 15" fill="none">
                <path d="M6.1584 3.13508C6.35985 2.94621 6.67627 2.95642 6.86514 3.15788L10.6151 7.15788C10.7954 7.3502 10.7954 7.64949 10.6151 7.84182L6.86514 11.8418C6.67627 12.0433 6.35985 12.0535 6.1584 11.8646C5.95694 11.6757 5.94673 11.3593 6.1356 11.1579L9.565 7.49985L6.1356 3.84182C5.94673 3.64036 5.95694 3.32394 6.1584 3.13508Z" fill="currentColor" />
              </svg>
            </button>
          </>
        )}

        {/* Image */}
        <div style={{
          position: 'relative',
          width: '100%',
          height: '140px',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, rgba(240, 240, 245, 0.5), rgba(230, 230, 240, 0.5))'
        }}>
          <img
            src={getImageUrl(currentArticle)}
            alt={currentArticle.title}
            onError={() => setImageError(prev => ({ ...prev, [currentIndex]: true }))}
            crossOrigin="anonymous"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '40px',
            background: 'linear-gradient(to top, rgba(255, 255, 255, 0.9), transparent)',
            pointerEvents: 'none'
          }} />
        </div>

        {/* Content */}
        <div style={{ padding: '14px' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '9px',
            fontWeight: '700',
            color: '#6b7280',
            textTransform: 'uppercase',
            padding: '4px 8px',
            background: 'rgba(243, 244, 246, 0.8)',
            borderRadius: '6px',
            marginBottom: '8px'
          }}>
            {currentArticle.source}
          </div>

          <h4 style={{
            fontSize: '13px',
            fontWeight: '600',
            color: '#1f2937',
            margin: '0 0 8px 0',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}>
            {currentArticle.title}
          </h4>

          <p style={{
            fontSize: '11px',
            color: '#6b7280',
            margin: '0 0 10px 0',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}>
            {currentArticle.snippet}
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '10px', color: '#9ca3af' }}>
            <span>{currentArticle.dateFormatted || 'Recently'}</span>
            {currentArticle.url && (
              <a href={currentArticle.url} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6' }}>
                Read
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Generate Podcast Button - Only show when NOT ready */}
      {podcastState !== 'ready' && (
        <button
          onClick={async () => {
            setIsGenerating(true);
            await onGeneratePodcast(searchResults);
            setIsGenerating(false);
          }}
          disabled={isGenerating || podcastState === 'generating'}
          style={{
            width: '100%',
            padding: '10px',
            background: (isGenerating || podcastState === 'generating') ? 'rgba(156, 163, 175, 0.7)' : '#272727',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            fontSize: '12px',
            fontWeight: '500',
            cursor: (isGenerating || podcastState === 'generating') ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s ease'
          }}
        >
          {(isGenerating || podcastState === 'generating') ? (
            <>
              <div style={{ width: '14px', height: '14px', border: '2px solid white', borderTop: '2px solid #272727', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              <span>Generating...</span>
            </>
          ) : (
            <span>Generate News Podcast</span>
          )}
        </button>
      )}

      {/* Play Now + Regenerate Buttons - Shows when podcast is ready */}
      {podcastState === 'ready' && audioUrl && (
        <div style={{ display: 'flex', gap: '8px' }}>
          {/* Regenerate Button */}
          <button
            onClick={async () => {
              setIsGenerating(true);
              await onGeneratePodcast(searchResults);
              setIsGenerating(false);
            }}
            disabled={isGenerating}
            style={{
              flex: '0 0 auto',
              width: '40px',
              height: '40px',
              padding: '0',
              background: 'rgba(39, 39, 39, 0)',
              border: '1px solid rgba(24, 24, 24, 0.2)',
              borderRadius: '8px',
              color: '#757575ff',
              cursor: isGenerating ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              if (!isGenerating) {
                e.currentTarget.style.background = 'rgba(39, 39, 39, 0.15)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(39, 39, 39, 0.1)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {isGenerating ? (
              <div style={{ width: '14px', height: '14px', border: '2px solid #272727', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            ) : (
<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.90321 7.29677C1.90321 10.341 4.11041 12.4147 6.58893 12.8439C6.87255 12.893 7.06266 13.1627 7.01355 13.4464C6.96444 13.73 6.69471 13.9201 6.41109 13.871C3.49942 13.3668 0.86084 10.9127 0.86084 7.29677C0.860839 5.76009 1.55996 4.55245 2.37639 3.63377C2.96124 2.97568 3.63034 2.44135 4.16846 2.03202L2.53205 2.03202C2.25591 2.03202 2.03205 1.80816 2.03205 1.53202C2.03205 1.25588 2.25591 1.03202 2.53205 1.03202L5.53205 1.03202C5.80819 1.03202 6.03205 1.25588 6.03205 1.53202L6.03205 4.53202C6.03205 4.80816 5.80819 5.03202 5.53205 5.03202C5.25591 5.03202 5.03205 4.80816 5.03205 4.53202L5.03205 2.68645L5.03054 2.68759L5.03045 2.68766L5.03044 2.68767L5.03043 2.68767C4.45896 3.11868 3.76059 3.64538 3.15554 4.3262C2.44102 5.13021 1.90321 6.10154 1.90321 7.29677ZM13.0109 7.70321C13.0109 4.69115 10.8505 2.6296 8.40384 2.17029C8.12093 2.11718 7.93465 1.84479 7.98776 1.56188C8.04087 1.27898 8.31326 1.0927 8.59616 1.14581C11.4704 1.68541 14.0532 4.12605 14.0532 7.70321C14.0532 9.23988 13.3541 10.4475 12.5377 11.3662C11.9528 12.0243 11.2837 12.5586 10.7456 12.968L12.3821 12.968C12.6582 12.968 12.8821 13.1918 12.8821 13.468C12.8821 13.7441 12.6582 13.968 12.3821 13.968L9.38205 13.968C9.10591 13.968 8.88205 13.7441 8.88205 13.468L8.88205 10.468C8.88205 10.1918 9.10591 9.96796 9.38205 9.96796C9.65819 9.96796 9.88205 10.1918 9.88205 10.468L9.88205 12.3135L9.88362 12.3123C10.4551 11.8813 11.1535 11.3546 11.7585 10.6738C12.4731 9.86976 13.0109 8.89844 13.0109 7.70321Z" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"></path></svg>
            )}
          </button>
          
          {/* Play Now Button */}
          <button
            onClick={onOpenPlayer}
            style={{
              flex: '1',
              padding: '10px',
              background: '#272727',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#1a1a1a';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#272727';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            🎧 Play Now
          </button>
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.6; transform: scale(1.2); } }
      `}</style>
    </div>
  );
};

export default NewsCard;